import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/seo/Seo";
import LiveScoresTicker from "@/components/feature/LiveScoresTicker";
import PredictionCard from "@/components/feature/PredictionCard";
import { predictionsService, liveService } from "@/api/services";
import { Button } from "@/components/ui/button";
import { ArrowRight, ChartLineUp, Crown, Lightning, Target } from "@phosphor-icons/react";
import EmptyPicks from "@/components/feature/EmptyPicks";
import PickOfTheDay from "@/components/feature/PickOfTheDay";
import PublicCaptainOfTheWeek from "@/components/feature/PublicCaptainOfTheWeek";

const HERO_BG =
    "https://images.unsplash.com/photo-1543326727-cf6c39e8f84c?crop=entropy&cs=srgb&fm=jpg&w=1920&q=80&ixlib=rb-4.0.3";
const STADIUM_BG =
    "https://images.unsplash.com/photo-1706675780107-7c43cc487928?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxmb290YmFsbCUyMHN0YWRpdW0lMjBuaWdodHxlbnwwfHx8fDE3Nzg5OTk4Mzl8MA&ixlib=rb-4.1.0&q=85";
const ANALYTICS_BG =
    "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBhbmFseXRpY3MlMjBkYXJrfGVufDB8fHx8MTc3ODk5OTgzOXww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [picks, setPicks] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [liveByFixture, setLiveByFixture] = useState({});

    // Scroll-to-anchor when arriving at /#features or /#plans from other pages.
    useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            const id = hash.slice(1);
            setTimeout(() => {
                const el = document.getElementById(id);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100);
        }
    }, []);

    useEffect(() => {
        if (user) {
            navigate("/dashboard", { replace: true });
            return;
        }
        let cancelled = false;
        let timer;

        const refreshLive = async (teaserPicks) => {
            const ids = teaserPicks.map((p) => p.fixtureId).filter(Boolean);
            if (!ids.length) return;
            try {
                const byFix = await liveService.byFixtures(ids);
                if (!cancelled)
                    setLiveByFixture(byFix && typeof byFix === "object" ? byFix : {});
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn("Landing live refresh failed:", err?.message || err);
            }
        };

        const load = async () => {
            try {
                const data = await predictionsService.today();
                const teaser = (data?.tiers?.["2"] || []).slice(0, 2);
                if (cancelled) return;
                setPicks(teaser);
                setGenerating(!!data?.generating);
                await refreshLive(teaser);
                timer = setInterval(() => refreshLive(teaser), 30000);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn("Landing teaser load failed:", err?.message || err);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        };
        load();
        return () => {
            cancelled = true;
            if (timer) clearInterval(timer);
        };
    }, [user, navigate]);

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "KickLoom",
        url: "https://kickloom.com",
        sameAs: [],
        description:
            "AI-powered football prediction platform delivering daily accumulator picks with live odds and confidence ratings.",
    };

    return (
        <Layout>
            <Seo path="/" jsonLd={jsonLd} />

            {/* HERO */}
            <section
                className="relative overflow-hidden border-b border-border"
                data-testid="hero-section"
            >
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.95) 100%), url(${HERO_BG})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
                    <div className="md:col-span-9">
                        <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mb-6">
                            ▌ DAILY · AI · FOOTBALL · INTELLIGENCE
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95] text-white">
                            Sharper picks.
                            <br />
                            <span className="text-primary neon-text">Cleaner odds.</span>
                            <br />
                            Built by AI.
                        </h1>
                        <p className="mt-8 text-base md:text-lg text-zinc-300 max-w-xl leading-relaxed">
                            KickLoom analyses thousands of fixtures, real bookmaker odds and
                            historical signals daily — then ships you the top 2, 5 and
                            10-game accumulator slips with confidence ratings.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/register" data-testid="hero-cta-register">
                                <Button
                                    size="lg"
                                    className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider px-8 h-12"
                                >
                                    Get Started Free
                                    <ArrowRight size={18} weight="bold" className="ml-2" />
                                </Button>
                            </Link>
                            <Link to="/login" data-testid="hero-cta-login">
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="rounded-none border-border hover:border-primary hover:text-primary uppercase tracking-wider px-8 h-12 bg-transparent"
                                >
                                    Login
                                </Button>
                            </Link>
                        </div>
                    </div>
                    <div className="md:col-span-3 hidden md:block">
                        <div className="border border-border bg-card/60 backdrop-blur p-6 space-y-4">
                            <Stat label="Fixtures / day" value="800+" />
                            <Stat label="Avg confidence" value="78%" />
                            <Stat label="Markets covered" value="24" />
                            <Stat label="Live update" value="60s" />
                        </div>
                    </div>
                </div>
            </section>

            <LiveScoresTicker />

            <PickOfTheDay />

            {/* FREE PICKS TEASER */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 py-20" id="features">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-10">
                    <div className="md:col-span-8">
                        <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mb-3">
                            ▌ FREE TODAY
                        </div>
                        <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-white">
                            Today's 2-Game Slip
                        </h2>
                    </div>
                    <p className="md:col-span-4 text-sm text-zinc-400 leading-relaxed md:text-right">
                        A taste of our highest-confidence picks. Register to unlock the
                        5 and 10-game accumulator tiers.
                    </p>
                </div>
                {picks.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {picks.map((p, i) => (
                            <PredictionCard
                                key={i}
                                pick={p}
                                live={
                                    p.fixtureId
                                        ? liveByFixture[String(p.fixtureId)]
                                        : undefined
                                }
                                testIdPrefix={`teaser-${i}`}
                            />
                        ))}
                    </div>
                ) : loaded ? (
                    <EmptyPicks
                        generating={generating}
                        message={
                            generating
                                ? "We're generating today's slip right now. Refresh in 10–30 seconds."
                                : "No fixtures scheduled in our covered leagues today. We'll be back tomorrow."
                        }
                    />
                ) : null}
            </section>

            <PublicCaptainOfTheWeek />

            {/* FEATURE BENTO */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 py-20">
                <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-white mb-12">
                    The KickLoom edge
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <FeatureCell
                        span="md:col-span-7"
                        icon={<ChartLineUp size={32} weight="duotone" />}
                        title="AI + real bookmaker odds"
                        body="Our engine cross-references AI-generated picks with live odds from API-Football, so the slips you see reflect the market — not a hallucination."
                        image={ANALYTICS_BG}
                    />
                    <FeatureCell
                        span="md:col-span-5"
                        icon={<Target size={32} weight="duotone" />}
                        title="Tiered slips"
                        body="2-game · 5-game · 10-game. Pick your appetite, scale your stake."
                    />
                    <FeatureCell
                        span="md:col-span-5"
                        icon={<Lightning size={32} weight="duotone" />}
                        title="Live 60s refresh"
                        body="Scores, kick-off times, statuses — all auto-refreshed every minute."
                    />
                    <FeatureCell
                        span="md:col-span-7"
                        icon={<Crown size={32} weight="duotone" />}
                        title="Quick custom predictions (VIP)"
                        body="Paste any fixtures you want analysed and our AI returns structured picks in seconds — markets, odds, confidence."
                        image={STADIUM_BG}
                    />
                </div>
            </section>

            {/* PLANS */}
            <section
                className="max-w-7xl mx-auto px-4 md:px-8 py-20"
                id="plans"
                data-testid="plans-section"
            >
                <div className="text-center mb-12">
                    <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mb-3">
                        ▌ PLANS
                    </div>
                    <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-white">
                        Two tiers. One winning rhythm.
                    </h2>
                    <p className="text-zinc-400 mt-3 text-sm md:text-base max-w-xl mx-auto">
                        Start free. Upgrade to VIP for the 5-game and 10-game accumulators.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                    <PlanCard
                        name="Basic"
                        price="Free"
                        features={[
                            "Today's 2-game slip",
                            "Live scores ticker",
                            "Match details modal",
                        ]}
                        cta="Start free"
                        to="/register"
                    />
                    <PlanCard
                        name="VIP"
                        highlight
                        price="₦1,000/mo"
                        features={[
                            "Everything in Basic",
                            "5-game and 10-game accumulators",
                            "Quick custom AI predictions",
                            "Priority support",
                        ]}
                        cta="Go VIP"
                        to="/register"
                    />
                </div>
            </section>
        </Layout>
    );
}

function Stat({ label, value }) {
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                {label}
            </div>
            <div className="font-mono text-2xl text-primary font-semibold mt-0.5">
                {value}
            </div>
        </div>
    );
}

function FeatureCell({ span, icon, title, body, image }) {
    return (
        <div
            className={`relative ${span} bg-card border border-border p-8 min-h-[220px] overflow-hidden group hover:border-primary/60 transition-snappy`}
        >
            {image && (
                <div
                    className="absolute inset-0 opacity-15 group-hover:opacity-25 transition-snappy"
                    style={{
                        backgroundImage: `linear-gradient(180deg, rgba(10,10,10,0.4) 0%, rgba(10,10,10,1) 100%), url(${image})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
            )}
            <div className="relative">
                <div className="text-primary mb-4">{icon}</div>
                <h3 className="text-xl md:text-2xl font-bold tracking-tight text-white mb-2">
                    {title}
                </h3>
                <p className="text-sm text-zinc-400 max-w-md leading-relaxed">{body}</p>
            </div>
        </div>
    );
}

function PlanCard({ name, price, features, cta, to, highlight }) {
    return (
        <div
            className={`bg-card border ${highlight ? "border-primary" : "border-border"} p-8 flex flex-col transition-snappy hover:-translate-y-1 ${highlight ? "neon-border" : ""}`}
            data-testid={`plan-${name.toLowerCase()}`}
        >
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold tracking-tight uppercase">{name}</h3>
                {highlight && (
                    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 bg-primary/10 text-primary border border-primary">
                        BEST
                    </span>
                )}
            </div>
            <div className="font-mono text-3xl font-bold mb-6">{price}</div>
            <ul className="space-y-2 mb-8 flex-1">
                {features.map((f) => (
                    <li key={f} className="text-sm text-zinc-300 flex gap-2">
                        <span className="text-primary mt-1">▌</span> {f}
                    </li>
                ))}
            </ul>
            <Link to={to}>
                <Button
                    className={`w-full rounded-none uppercase tracking-wider font-bold ${
                        highlight
                            ? "bg-primary text-primary-foreground hover:bg-[#33FFB0]"
                            : "bg-secondary text-white hover:bg-primary hover:text-primary-foreground"
                    }`}
                >
                    {cta}
                </Button>
            </Link>
        </div>
    );
}
